import { useState } from "react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@capacit-com/lara-law-shared-ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@capacit-com/lara-law-shared-ui";
import { Check, ChevronDown, MoreHorizontal, UserPlus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@capacit-com/lara-law-shared-ui";
import { WorkspaceRole } from "@/models/workspace/Workspace";
import useWorkspace from "@/hooks/useWorkspace";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import UserAvatar from "@/components/images/UserAvatar";
import { formatUsersName } from "@/utils/format";
import { useWorkspaceContext } from "../providers/WorkspaceContext";
import { Spinner } from "@capacit-com/lara-law-shared-ui";

export const WorkspaceUsers: React.FC = () => {
  const { workspace, tenantUsers, myUser } = useWorkspaceContext();
  const { grantAccess, revokeAccess } = useWorkspace();
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteRole, setInviteRole] = useState<WorkspaceRole>(
    WorkspaceRole.READ
  );
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const handleRoleChange = (userId: string, newRole: WorkspaceRole) => {
    try {
      grantAccess.mutate({
        workspaceId: workspace!.id,
        userIds: [userId],
        role: newRole,
      });
    } catch (error) {
      logger.error("Failed to change workspace user role", {
        operation: "changeWorkspaceUserRole",
        error,
        workspaceId: workspace?.id,
        userId,
        role: newRole,
      });
    }
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      await revokeAccess.mutateAsync({
        workspaceId: workspace!.id,
        userId,
      });
    } catch (error) {
      logger.error("Failed to remove user from workspace", {
        operation: "removeWorkspaceUser",
        error,
        workspaceId: workspace?.id,
        userId,
      });
    }
  };

  const handleInviteUsers = async () => {
    try {
      await grantAccess.mutateAsync({
        workspaceId: workspace!.id,
        userIds: selectedUsers,
        role: inviteRole,
      });
      setIsInviteOpen(false);
      setSelectedUsers([]);
      setInviteRole(WorkspaceRole.READ);
    } catch (error) {
      logger.error("Failed to invite users to workspace", {
        operation: "inviteWorkspaceUsers",
        error,
        workspaceId: workspace?.id,
        userCount: selectedUsers.length,
        role: inviteRole,
      });
      // Show a more specific error message for backend issues
      if (error instanceof Error && error.message.includes("projection")) {
        showErrorToast(
          "Der skete en fejl på serveren. Prøv igen senere eller kontakt support."
        );
      } else {
        showErrorToast("Der skete en fejl under invitation af brugere.");
      }
    }
  };

  const getRoleBadgeVariant = (role: WorkspaceRole) => {
    switch (role) {
      case WorkspaceRole.ADMIN:
        return "default";
      case WorkspaceRole.WRITE:
        return "secondary";
      case WorkspaceRole.READ:
        return "outline";
    }
  };

  const getUsersWithAccess = () => {
    const userIds = Object.keys(workspace!.sharedAccess);
    return tenantUsers.filter((user) => userIds.includes(user.id));
  };

  const getUsersWithoutAccess = () => {
    const userIds = Object.keys(workspace!.sharedAccess);
    return tenantUsers.filter(
      (user) => !userIds.includes(user.id) && user.id !== myUser?.id
    );
  };

  const getOwner = () => {
    return tenantUsers.find((user) => user.id === workspace!.userId);
  };

  const getUserRole = (userId: string) => {
    return workspace!.sharedAccess[userId];
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Workspace medlemmer</h3>
          <p className="text-sm text-muted-foreground">
            Administrer brugere og deres rettigheder i dette workspace
          </p>
        </div>
        <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="mr-2 size-4" />
              Inviter brugere
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Inviter nye brugere</DialogTitle>
              <DialogDescription>
                Vælg brugere du vil invitere til dette workspace og tildel dem
                rettigheder.
              </DialogDescription>
            </DialogHeader>
            <Command className="border">
              <CommandInput placeholder="Søg efter brugere..." />
              <CommandList className="h-[300px]">
                <CommandEmpty className="flex items-center justify-center">
                  Ingen brugere fundet.
                </CommandEmpty>
                <CommandGroup>
                  {getUsersWithoutAccess().map((user) => (
                    <CommandItem
                      key={user.id}
                      value={
                        user.firstName && user.lastName
                          ? user.firstName +
                            " " +
                            user.lastName +
                            " - " +
                            user.id
                          : user.email + " - " + user.id
                      }
                      onSelect={() => {
                        setSelectedUsers((prev) =>
                          prev.includes(user.id)
                            ? prev.filter((item) => item !== user.id)
                            : [...prev, user.id]
                        );
                      }}
                      className="group"
                    >
                      <div className="flex items-center gap-2">
                        <UserAvatar
                          user={user}
                          fallbackClassName="text-[10px] group-hover:text-foreground"
                        />
                        <span>{formatUsersName(user)}</span>
                      </div>
                      <Check
                        className={`ml-auto size-4 ${
                          selectedUsers.includes(user.id)
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
            <Select
              value={inviteRole.toString()}
              onValueChange={(value: string) =>
                setInviteRole(value as WorkspaceRole)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Vælg rettigheder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={WorkspaceRole.READ}>Læse adgang</SelectItem>
                <SelectItem value={WorkspaceRole.WRITE}>
                  Upload adgang
                </SelectItem>
                <SelectItem value={WorkspaceRole.ADMIN}>
                  Administrator
                </SelectItem>
              </SelectContent>
            </Select>
            <Button
              className="w-full"
              onClick={handleInviteUsers}
              disabled={selectedUsers.length === 0 || grantAccess.isPending}
            >
              {grantAccess.isPending ? (
                <Spinner className="size-4 text-primary-foreground" />
              ) : null}
              {grantAccess.isPending
                ? "Inviterer brugere..."
                : "Inviter valgte brugere (" + selectedUsers.length + ")"}
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-lg overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bruger</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rettigheder</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Get the owner first */}
            {(() => {
              const owner = getOwner();
              return (
                owner && (
                  <TableRow key={owner.id}>
                    <TableCell className="flex items-center gap-2">
                      <UserAvatar user={owner} avatarClassName="size-8" />
                      {formatUsersName(owner)}
                    </TableCell>
                    <TableCell>{owner.email}</TableCell>
                    <TableCell>
                      <Button variant="default" size="sm" disabled>
                        Ejer
                      </Button>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )
              );
            })()}

            {/* Render other users with access */}
            {getUsersWithAccess()
              .filter((user) => user.id !== workspace!.userId) // Exclude the owner
              .map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="flex items-center gap-2">
                    <UserAvatar user={user} avatarClassName="size-8" />
                    {formatUsersName(user)}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant={getRoleBadgeVariant(getUserRole(user.id))}
                          size="sm"
                          disabled={user.id === myUser?.id}
                        >
                          {getUserRole(user.id) === WorkspaceRole.ADMIN
                            ? "Administrator"
                            : getUserRole(user.id) === WorkspaceRole.WRITE
                              ? "Upload adgang"
                              : "Læse adgang"}
                          {user.id !== myUser?.id && (
                            <ChevronDown className="size-4" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            handleRoleChange(user.id, WorkspaceRole.READ)
                          }
                        >
                          Læse adgang
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleRoleChange(user.id, WorkspaceRole.WRITE)
                          }
                        >
                          Upload adgang
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleRoleChange(user.id, WorkspaceRole.ADMIN)
                          }
                        >
                          Administrator
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                  {
                    // Do not allow the current user to remove themselves
                    user.id !== myUser?.id && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="size-8 p-0">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleRemoveUser(user.id)}
                            >
                              Fjern bruger
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )
                  }
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default WorkspaceUsers;
